/** A repository the user can start a conversation in: its name and absolute path. */
export type RepositoryOption = {
  name: string;
  path: string;
  /** Where its folder is: this computer's, or only the cloud's copy. */
  origin?: RepositoryOrigin;
};

/**
 * A repository's folder and forge: a folder of this computer, or a copy only the cloud has, and
 * the forge repository it clones (`owner/name`, and its address when known). A forge repository
 * the cloud has can be linked to a folder of this computer: it joins its project.
 */
export type RepositoryOrigin = {
  onThisComputer: boolean;
  forge: { fullName: string; remote: string | null } | null;
  /** Its project, which the linked folder joins (`null`: a folder no project knows yet). */
  logicalWorkspaceId: string | null;
};

/**
 * The distinct workspace roots offered in the "start here" repository picker: every project, by
 * its sidebar label, plus the active workspace by the server's name (`nameOf`), de-duplicated and
 * sorted by name. A worktree is never named by its own folder.
 */
export function buildRepositoryOptions(
  projectPaths: ReadonlyMap<string, string>,
  workspace: string | null,
  nameOf: (path: string) => string,
  originOf?: (path: string) => RepositoryOrigin,
): readonly RepositoryOption[] {
  const options = new Map<string, string>();
  for (const [label, path] of projectPaths) if (!options.has(path)) options.set(path, label);
  if (workspace !== null && !options.has(workspace)) options.set(workspace, nameOf(workspace));
  return [...options]
    .map(([path, name]) =>
      originOf === undefined ? { name, path } : { name, path, origin: originOf(path) },
    )
    .sort((left, right) => left.name.localeCompare(right.name));
}
